import React from 'react';
import { ShieldCheck, GraduationCap, ArrowRight, Lock, FileText, BookOpen, Activity, ExternalLink, Eye } from 'lucide-react';

interface MainMenuProps {
  onSelectInduction: () => void;
  onSelectMandatoryTraining?: () => void;
  onSelectMandatory?: () => void;
  onSelectAdmin?: () => void;
  onToggleLang?: (lang: 'id' | 'en') => void;
  lang: 'id' | 'en';
}

export const MainMenu: React.FC<MainMenuProps> = ({
  onSelectInduction,
  onSelectMandatoryTraining,
  onSelectMandatory,
  onSelectAdmin,
  onToggleLang,
  lang
}) => {
  const handleMandatoryClick = () => {
    if (onSelectMandatoryTraining) {
      onSelectMandatoryTraining();
    } else if (onSelectMandatory) {
      onSelectMandatory();
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-3 sm:px-6 py-4 sm:py-8 space-y-5 sm:space-y-8 animate-fade-in">
      {/* Header Banner - Simple & Modern */}
      <div className="bg-gradient-to-r from-slate-900 via-blue-950 to-slate-900 rounded-2xl sm:rounded-3xl p-4 sm:p-8 text-white shadow-lg relative overflow-hidden border border-slate-800">
        <div className="absolute -right-10 -bottom-10 w-48 h-48 bg-blue-500/10 rounded-full blur-2xl pointer-events-none" />

        <div className="relative z-10 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="space-y-1.5">
            <h1 className="text-xl sm:text-3xl font-extrabold tracking-tight text-white">
              EHS Marketing Portal
            </h1>
            <p className="text-slate-300 text-xs sm:text-sm max-w-xl">
              {lang === 'id'
                ? 'Selamat Datang di Area Marketing Divisi, Pilih menu yang ingin Anda akses di bawah ini.'
                : 'Welcome to the Division Marketing Area. Select the menu you wish to access below.'}
            </p>
          </div>

          {/* Action Header Controls */}
          <div className="flex items-center gap-2 self-start sm:self-center shrink-0">
            {onToggleLang && (
              <div className="bg-slate-800 p-1 rounded-xl flex items-center border border-slate-700">
                <button
                  type="button"
                  onClick={() => onToggleLang('id')}
                  className={`px-2.5 py-1 rounded-lg text-xs font-extrabold transition ${
                    lang === 'id' ? 'bg-amber-500 text-white shadow-xs' : 'text-slate-400 hover:text-white'
                  }`}
                >
                  ID
                </button>
                <button
                  type="button"
                  onClick={() => onToggleLang('en')}
                  className={`px-2.5 py-1 rounded-lg text-xs font-extrabold transition ${
                    lang === 'en' ? 'bg-amber-500 text-white shadow-xs' : 'text-slate-400 hover:text-white'
                  }`}
                >
                  EN
                </button>
              </div>
            )}

            {onSelectAdmin && (
              <button
                onClick={onSelectAdmin}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-slate-800 hover:bg-slate-700 border border-slate-700 text-slate-200 text-xs font-bold transition cursor-pointer shadow-xs"
              >
                <Lock className="w-3.5 h-3.5 text-amber-400" />
                <span>Admin</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Grid Columns for Mobile & Desktop */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5 sm:gap-6">
        
        {/* Card 1: Safety Induction */}
        <div 
          onClick={onSelectInduction}
          className="group bg-white rounded-2xl sm:rounded-3xl p-3.5 sm:p-7 border-2 border-slate-200 hover:border-blue-600 shadow-sm hover:shadow-xl transition-all duration-200 cursor-pointer flex flex-col justify-between overflow-hidden relative"
        >
          <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-blue-500 to-indigo-600 group-hover:h-2 transition-all" />

          <div className="space-y-3 sm:space-y-5">
            <div className="flex items-center justify-between">
              <div className="w-10 h-10 sm:w-14 sm:h-14 rounded-xl sm:rounded-2xl bg-blue-50 text-blue-600 flex items-center justify-center border border-blue-100 group-hover:bg-blue-600 group-hover:text-white transition-all shadow-2xs">
                <ShieldCheck className="w-6 h-6 sm:w-8 sm:h-8" />
              </div>
              <span className="hidden sm:inline-block px-2.5 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-200 text-[10px] sm:text-xs font-extrabold uppercase">
                ALL
              </span>
            </div>

            <div className="space-y-1">
              <h2 className="text-sm sm:text-2xl font-extrabold text-slate-900 group-hover:text-blue-600 transition-colors leading-tight">
                Safety Induksi
              </h2>
              <p className="text-slate-500 text-[11px] sm:text-sm leading-relaxed">
                {lang === 'id'
                  ? 'Formulir Evaluasi Dan Post test Safety Induksi untuk Tamu, Contractor, Vendor, & Pekerja Operasional.'
                  : 'Evaluation and Post-Test Safety Induction Form for Guests, Contractors, Vendors, & Operational Workers.'}
              </p>
            </div>

            <div className="hidden sm:block pt-2 border-t border-slate-100">
              <span className="inline-flex items-center gap-1.5 text-xs text-slate-600 font-medium">
                <FileText className="w-3.5 h-3.5 text-blue-600" />
                <span>Materi, Kuis & Sertifikat PDF</span>
              </span>
            </div>
          </div>

          <div className="mt-4 sm:mt-6 pt-2 sm:pt-4 border-t border-slate-100 flex items-center justify-between text-blue-600 font-extrabold text-xs sm:text-sm">
            <span className="truncate">
              {lang === 'id' ? 'Mulai mengisi' : 'Start Filling'}
            </span>
            <div className="w-6 h-6 sm:w-8 sm:h-8 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center group-hover:bg-blue-600 group-hover:text-white transition-all shrink-0">
              <ArrowRight className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
            </div>
          </div>
        </div>

        {/* Card 2: Safety Mandatory Training (For Internal) */}
        <div 
          onClick={handleMandatoryClick}
          className="group bg-white rounded-2xl sm:rounded-3xl p-3.5 sm:p-7 border-2 border-slate-200 hover:border-amber-500 shadow-sm hover:shadow-xl transition-all duration-200 cursor-pointer flex flex-col justify-between overflow-hidden relative"
        >
          <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-amber-500 to-orange-600 group-hover:h-2 transition-all" />

          <div className="space-y-3 sm:space-y-5">
            <div className="flex items-center justify-between">
              <div className="w-10 h-10 sm:w-14 sm:h-14 rounded-xl sm:rounded-2xl bg-amber-50 text-amber-600 flex items-center justify-center border border-amber-100 group-hover:bg-amber-500 group-hover:text-white transition-all shadow-2xs">
                <GraduationCap className="w-6 h-6 sm:w-8 sm:h-8" />
              </div>
              <span className="px-2 py-0.5 rounded-md bg-amber-100 text-amber-800 text-[10px] sm:text-xs font-extrabold border border-amber-200 shrink-0">
                INTERNAL
              </span>
            </div>

            <div className="space-y-1">
              <h2 className="text-sm sm:text-2xl font-extrabold text-slate-900 group-hover:text-amber-600 transition-colors leading-tight">
                Safety Mandatory Training
              </h2>
              <p className="text-slate-500 text-[11px] sm:text-sm leading-relaxed">
                {lang === 'id'
                  ? 'Modul pelatihan keselamatan wajib: JSA, Defensive Driving, LOTO, dll.'
                  : 'Mandatory worker safety training: JSA, Defensive Driving, LOTO, etc.'}
              </p>
            </div>

            <div className="hidden sm:block pt-2 border-t border-slate-100">
              <span className="inline-flex items-center gap-1.5 text-xs text-slate-600 font-medium">
                <BookOpen className="w-3.5 h-3.5 text-amber-600" />
                <span>Drive Viewer & Evaluation Test</span>
              </span>
            </div>
          </div>

          <div className="mt-4 sm:mt-6 pt-2 sm:pt-4 border-t border-slate-100 flex items-center justify-between text-amber-600 font-extrabold text-xs sm:text-sm">
            <span className="truncate">
              {lang === 'id' ? 'Mulai Training' : 'Start Training'}
            </span>
            <div className="w-6 h-6 sm:w-8 sm:h-8 rounded-full bg-amber-50 text-amber-600 flex items-center justify-center group-hover:bg-amber-500 group-hover:text-white transition-all shrink-0">
              <ArrowRight className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
            </div>
          </div>
        </div>

        {/* Card 3: Fatigue Assessment System ( FAS ) */}
        <div 
          onClick={() => window.open('https://fatiguecheck.pages.dev', '_blank')}
          className="group bg-white rounded-2xl sm:rounded-3xl p-3.5 sm:p-7 border-2 border-slate-200 hover:border-emerald-500 shadow-sm hover:shadow-xl transition-all duration-200 cursor-pointer flex flex-col justify-between overflow-hidden relative"
        >
          <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-emerald-500 to-teal-600 group-hover:h-2 transition-all" />

          <div className="space-y-3 sm:space-y-5">
            <div className="flex items-center justify-between">
              <div className="w-10 h-10 sm:w-14 sm:h-14 rounded-xl sm:rounded-2xl bg-emerald-50 text-emerald-600 flex items-center justify-center border border-emerald-100 group-hover:bg-emerald-600 group-hover:text-white transition-all shadow-2xs">
                <Activity className="w-6 h-6 sm:w-8 sm:h-8" />
              </div>
              <span className="px-2 py-0.5 rounded-md bg-emerald-100 text-emerald-800 text-[10px] sm:text-xs font-extrabold border border-emerald-200 shrink-0 uppercase">
                FATIGUE CHECK
              </span>
            </div>

            <div className="space-y-1">
              <h2 className="text-sm sm:text-2xl font-extrabold text-slate-900 group-hover:text-emerald-600 transition-colors leading-tight">
                Fatigue Assessment System ( FAS )
              </h2>
              <p className="text-slate-500 text-[11px] sm:text-sm leading-relaxed">
                {lang === 'id'
                  ? 'Sistem Penilaian Tingkat Kelelahan & Kesiapan Kerja (Fatigue Check) Sebelum Memulai Pekerjaan Operasional.'
                  : 'Fatigue & Work Readiness Assessment System (Fatigue Check) Before Starting Operational Tasks.'}
              </p>
            </div>

            <div className="hidden sm:block pt-2 border-t border-slate-100">
              <span className="inline-flex items-center gap-1.5 text-xs text-slate-600 font-medium">
                <Activity className="w-3.5 h-3.5 text-emerald-600" />
                <span>Online Fatigue Check Form</span>
              </span>
            </div>
          </div>

          <div className="mt-4 sm:mt-6 pt-2 sm:pt-4 border-t border-slate-100 flex items-center justify-between text-emerald-600 font-extrabold text-xs sm:text-sm">
            <span className="truncate">
              {lang === 'id' ? 'Mulai Mengisi' : 'Start Filling'}
            </span>
            <div className="w-6 h-6 sm:w-8 sm:h-8 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center group-hover:bg-emerald-600 group-hover:text-white transition-all shrink-0">
              <ExternalLink className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
            </div>
          </div>
        </div>

        {/* Card 4: Behavior Based Safety ( BBS ) */}
        <div 
          onClick={() => window.open('https://bbs-ehsmkt.pages.dev/', '_blank')}
          className="group bg-white rounded-2xl sm:rounded-3xl p-3.5 sm:p-7 border-2 border-slate-200 hover:border-purple-500 shadow-sm hover:shadow-xl transition-all duration-200 cursor-pointer flex flex-col justify-between overflow-hidden relative"
        >
          <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-purple-500 to-indigo-600 group-hover:h-2 transition-all" />

          <div className="space-y-3 sm:space-y-5">
            <div className="flex items-center justify-between">
              <div className="w-10 h-10 sm:w-14 sm:h-14 rounded-xl sm:rounded-2xl bg-purple-50 text-purple-600 flex items-center justify-center border border-purple-100 group-hover:bg-purple-600 group-hover:text-white transition-all shadow-2xs">
                <Eye className="w-6 h-6 sm:w-8 sm:h-8" />
              </div>
              <span className="px-2 py-0.5 rounded-md bg-purple-100 text-purple-800 text-[10px] sm:text-xs font-extrabold border border-purple-200 shrink-0 uppercase">
                BBSQ
              </span>
            </div>

            <div className="space-y-1">
              <h2 className="text-sm sm:text-2xl font-extrabold text-slate-900 group-hover:text-purple-600 transition-colors leading-tight">
                Behavior Based Safety ( BBS )
              </h2>
              <p className="text-slate-500 text-[11px] sm:text-sm leading-relaxed">
                {lang === 'id'
                  ? 'Sistem Observasi & Pelaporan Perilaku Keselamatan Kerja (Behavior Based Safety) oleh Tim Leader Yard.'
                  : 'Safety Behavior Observation & Reporting System (Behavior Based Safety) by Team leader Yard.'}
              </p>
            </div>

            <div className="hidden sm:block pt-2 border-t border-slate-100">
              <span className="inline-flex items-center gap-1.5 text-xs text-slate-600 font-medium">
                <Eye className="w-3.5 h-3.5 text-purple-600" />
                <span>Online BBS Observation Form</span>
              </span>
            </div>
          </div>

          <div className="mt-4 sm:mt-6 pt-2 sm:pt-4 border-t border-slate-100 flex items-center justify-between text-purple-600 font-extrabold text-xs sm:text-sm">
            <span className="truncate">
              {lang === 'id' ? 'Mulai Mengisi' : 'Start Filling'}
            </span>
            <div className="w-6 h-6 sm:w-8 sm:h-8 rounded-full bg-purple-50 text-purple-600 flex items-center justify-center group-hover:bg-purple-600 group-hover:text-white transition-all shrink-0">
              <ExternalLink className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
            </div>
          </div>
        </div>

      </div>

      {/* Footer Info Badge */}
      <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 sm:p-4 text-center">
        <p className="text-[11px] sm:text-xs text-slate-500 font-semibold">
          PT United Tractors Tbk • EHS Marketing Division @2026
        </p>
      </div>
    </div>
  );
};
